<template>
  <div class="flow-details flow-details-path">
    <div class="flow-details__title">
      <div class="subtitle">
        {{ t('Path settings') }}
      </div>
      <div class="flow-details__header-action-wr">
        <a class="flow-details__duplicate" @click="emit('duplicate', rule, path)">
          <ui-icon size="12" class="mr-4" :color="$colors.blue" icon="fas fa-copy"></ui-icon>
          {{ t('Duplicate Path') }}
        </a>
        <a v-if="showDelete" class="flow-details__remove" @click="emit('delete', rule, path)">
          <ui-icon size="12" class="mr-4" :color="$colors.red" icon="far fa-trash-alt"></ui-icon>
          {{ t('Delete Path') }}
        </a>
        <div class="flow-details__action-wr ml-4">
          <ui-switcher v-model="pathActive" :disabled="activeSwitchDisabled">
            <template #label>
              <span v-if="pathActive">{{ t('Active') }}</span>
              <span v-if="!pathActive">{{ t('Inactive') }}</span>
            </template>
          </ui-switcher>
        </div>
      </div>
    </div>

    <div class="flow-details__wrap flow-details-path__wrap">
      <div class="flow-details__inp-wr flow-details-path__controls">
        <div class="flow-details-path__inputs d-flex">
          <div class="flow-details__inp-name pl-10">
            <ui-input
              v-model="path.name"
              :placeholder="t('Name')"
              :label="t('Name') + '*'"
              name="name"
              :hide-details="1"
              :error-messages="nameErrorMessages"
            ></ui-input>
          </div>

          <div class="flow-details__inp-weight pr-10">
            <ui-input
              v-model="path.weight"
              :placeholder="t('Weight')"
              :hide-details="1"
              type="number"
              :error-messages="weightErrorMessages"
            >
              <template #label>
                <ui-label
                  class="ml-4 ml-first-0"
                  :text="t('Weight')"
                ></ui-label>
                <ui-tooltip-icon
                  v-if="activeSwitchDisabled"
                  class="ml-4 ml-first-0"
                  preset="warning"
                  :text="t('Weight = 0, the Path is disabled')"
                ></ui-tooltip-icon>
              </template>
            </ui-input>
          </div>
        </div>
      </div>
      <div class="flow-details-path__columns d-flex">
        <div
          :class="[
            'flow-details__right-component-table',
            'flow-details__left-component-table',
            'flow-details-path__path-card-wrapper',
          ]"
        >
          <path-card
            type="landings"
            :rule-index="ruleIndex"
            :path-index="pathIndex"
            :form="form"
            :path="path"
            :all-workspace-ids="allWorkspaceIds"
            :offer-options="offerOptions"
            :landing-options="landingOptions"
            :errors-raw="errorsRaw"
            :path-error="isEmptyLandingsError"
          ></path-card>
        </div>

        <div
          :class="[
            'flow-details__right-component-table',
            'flow-details-path__path-card-wrapper',
          ]"
        >
          <path-card
            type="offers"
            :rule-index="ruleIndex"
            :path-index="pathIndex"
            :form="form"
            :path="path"
            :errors-raw="errorsRaw"
            :path-error="isEmptyOffersError"
            :offer-options="offerOptions"
            :landing-options="landingOptions"
          ></path-card>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useColors } from '@/composables';
import PathCard from './path-card.vue';
import { useFlowPathSettings } from './useFlowPathSettings';

const $colors = useColors();

const props = defineProps<{
  rule: {
    default?: boolean;
    paths: Array<{
      name?: string;
      weight: number;
      status?: string;
      landings: Array<{ id: string; weight?: number }>;
      offers: Array<{ id: string; weight: number }>;
    }>;
  };
  ruleIndex: number;
  pathIndex: number;
  path: {
    name?: string;
    weight: number;
    status?: string;
    landings: Array<{ id: string; weight?: number }>;
    offers: Array<{ id: string; weight: number }>;
  };
  allWorkspaceIds: unknown;
  form: {
    workspaceId: string;
    rules: Array<{
      paths: Array<{
        landings: Array<{ id: string; weight?: number }>;
        offers: Array<{ id: string; weight: number }>;
      }>;
    }>;
  };
  offerOptions: Offer[];
  landingOptions: Landing[];
  errorsDict?: Record<string, string[]>;
  errorsRaw: unknown[][] | null;
}>();

const emit = defineEmits<{
  duplicate: [rule: typeof props.rule, path: typeof props.path];
  delete: [rule: typeof props.rule, path: typeof props.path];
  change: [status: string | undefined];
}>();

const {
  t,
  pathActive,
  activeSwitchDisabled,
  showDelete,
  isEmptyLandingsError,
  isEmptyOffersError,
  nameErrorMessages,
  weightErrorMessages,
} = useFlowPathSettings(props, emit);
</script>

<style lang="scss" scoped>
@import '@/styles/dialogs/report-modals';

.subtitle {
  border-bottom: none;
}
</style>

<style lang="scss">
.flow-details-path {
  &__wrap {
    display: flex;
    flex-direction: column;
  }

  &__controls {
    flex-grow: 0;
  }

  &__inputs {
    flex-wrap: wrap;

    // Former grid: Name lg4/md4, Weight lg2/md4, both xs12
    .flow-details__inp-name {
      flex: 0 0 33.333%;
      max-width: 33.333%;
      min-width: 0;
    }

    .flow-details__inp-weight {
      flex: 0 0 16.667%;
      max-width: 16.667%;
      min-width: 0;
    }

    @media (max-width: 1263px) {
      .flow-details__inp-name,
      .flow-details__inp-weight {
        flex: 0 0 33.333%;
        max-width: 33.333%;
      }
    }

    @media (max-width: 959px) {
      .flow-details__inp-name,
      .flow-details__inp-weight {
        flex: 0 0 100%;
        max-width: 100%;
      }
    }
  }

  &__columns {
    > .flow-details-path__path-card-wrapper {
      flex: 1;
      min-width: 0;
    }
  }

  &__path-card-wrapper {
    display: flex;
    flex-direction: column;
  }

  &__weight-label {
    display: flex;
    align-items: baseline;
  }

  &__weight-label-tooltip {
    display: block;
    margin-left: 5px;
  }
}
</style>
